// Contacts.js
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { setSelectedVendor } from "../../../../features/vendorSlice";
import { getVendorDetailsById, updateSalesContact } from "../../../../api/sales";
import axiosInstance from "../../../../api/axiosInstance";
import { formatPhone, getUserInfoDetails } from "../../../../utlis";
import Pencil from "../../../../images/pencil.png";
import { useForm } from "react-hook-form";
import { useVendorForm } from "./../VendorFormContext";
import Modal from "../../../../components/ModalComponents/Modal";
import SalesContacts from "../../Contacts/SalesContacts";
import SalesContactsModal from "../../Contacts/components/SalesContactsModal";

// Extract phone number from contact object (handles different API structures)
const getPhoneNumber = (contact) => {
  // Try different possible phone fields
  if (contact.mobileNum1) return contact.mobileNum1;
  if (contact.phone?.mobile) return contact.phone.mobile;
  if (contact.phone?.phone) return contact.phone.phone;
  if (typeof contact.phone === 'string') return contact.phone;
  if (contact.mobile) return contact.mobile;
  if (contact.phoneNumber) return contact.phoneNumber;
  if (contact.phone_number) return contact.phone_number;
  return '';
};

const Contacts = () => {
  const navigate = useNavigate();
  const { formData, updateFormData, saveVendorData, saveAllChanges, hasUnsavedChanges, isCreating, isSaving, markTabComplete } = useVendorForm();
  const dispatch = useDispatch();
  const selectedVendor = useSelector((state) => state.vendor.selectedVendor);
  const location = useLocation();
  const params = useParams();

  // Ensure selectedVendor is set on direct navigation/refresh
  useEffect(() => {
    if (!selectedVendor) {
      const vendorId = location.state?.vendorId || params.id;
      if (vendorId) {
        getVendorDetailsById(vendorId).then((vendor) => {
          if (vendor) dispatch(setSelectedVendor(vendor));
        });
      }
    }
  }, [selectedVendor, location.state, params.id, dispatch]);

  // Sync context contacts with selected vendor's contacts
  useEffect(() => {
    if (
      selectedVendor &&
      Array.isArray(selectedVendor.contacts) &&
      (!formData.contacts || formData.contacts.length === 0)
    ) {
      // Transform contacts to ensure phone number and isPrimary are properly mapped
      const transformedContacts = selectedVendor.contacts.map((c, index) => ({
        ...c,
        firstName: c.firstName || c.first_name || '',
        lastName: c.lastName || c.last_name || '',
        mobileNum1: getPhoneNumber(c),
        isPrimary: c.isPrimary || c.contact_type === 'Primary' || index === 0,
      }));
      updateFormData("contacts", transformedContacts);
    } else if (!selectedVendor) {
      updateFormData("contacts", []);
    }
  }, [selectedVendor, updateFormData]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: formData?.contacts || {},
  });

  const [isContactsOpen, setIsContactsModalOpen] = useState(false);
  // Local contacts state, only update from context on mount or vendor change
  const [contacts, setContacts] = useState([]);
  const [pendingAdd, setPendingAdd] = useState(null);
  // Track last saved contacts for dirty check
  const [lastSavedContacts, setLastSavedContacts] = useState(Array.isArray(formData?.contacts) ? formData.contacts : []);
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [allContactsData, setAllContactsData] = useState([]);

  const handleContactSelect = (contact) => {
    const transformedContact = {
      contact_id: contact.contact_id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      mobileNum1: contact.phone?.mobile || "",
      company: contact.jobTitle || "",
      isPrimary: contacts.length === 0, // First contact is automatically primary
    };
    // Prevent duplicate before closing modal or setting pendingAdd
    const isDuplicate = contacts.some(
      (c) => {
        const existingId = c.contact_id || c.contactId;
        return String(existingId) === String(transformedContact.contact_id);
      }
    );
    if (isDuplicate) {
      toast.warning("This contact has already been added");
      setIsContactsModalOpen(false);
      return;
    }
    setPendingAdd(transformedContact);
    setIsContactsModalOpen(false);
  };

  // Handle making a contact primary
  const handleMakePrimary = (contactId) => {
    setContacts((prevContacts) => {
      const updatedContacts = prevContacts.map((contact) => ({
        ...contact,
        isPrimary: String(contact.contact_id || contact.contactId) === String(contactId),
      }));
      updateFormData("contacts", updatedContacts);
      return updatedContacts;
    });
    toast.success("Primary contact updated");
  };

  // Fetch all contacts data for editing
  useEffect(() => {
    const fetchAllContacts = async () => {
      try {
        const { companyId, clientId } = getUserInfoDetails();
        const res = await axiosInstance.get('/contacts', {
          params: { company: companyId, client: clientId },
        });
        setAllContactsData(res.data.data.records || []);
      } catch (err) {
        // Logged for debugging in development only
      }
    };
    fetchAllContacts();
  }, []);

  // Handle opening edit modal for a contact
  const handleEditContact = (contact) => {
    const contactId = contact.contact_id || contact.contactId;
    // Find full contact data from allContactsData
    const fullContactData = allContactsData.find((c) => c.contact_id === contactId);

    if (!fullContactData) {
      toast.error('Contact details not found. Try refreshing the page.');
      return;
    }

    const createdDate = new Date(fullContactData.created);
    const updatedDate = new Date(fullContactData.updated);

    const contactData = {
      firstName: fullContactData.first_name,
      middleName: fullContactData.middle_name,
      lastName: fullContactData.last_name,
      preferredName: fullContactData.preferred_name,
      email: fullContactData.email,
      jobTitle: fullContactData.job_title,
      mobileNum1: fullContactData?.phone?.mobile,
      mobileNum2: fullContactData?.phone?.phone,
      fax: fullContactData?.phone?.Fax || '',
      contactType: fullContactData.contact_type || '',
      address1: fullContactData.address?.address_line1,
      address2: fullContactData.address?.address_line2,
      city: fullContactData.address?.city,
      state: fullContactData.address?.state,
      country: fullContactData.address?.country,
      zipCode: fullContactData.address?.zip,
      notes: fullContactData.note || '',
      vendorStatus: fullContactData.status || 'Active',
      contact_id: fullContactData.contact_id,
      createdDate: createdDate.toISOString().split('T')[0] || '',
      createdTime: createdDate.toISOString().split('T')[1]?.slice(0, 5) || '',
      updateDate: updatedDate.toISOString().split('T')[0] || '',
      updateTime: updatedDate.toISOString().split('T')[1]?.slice(0, 5) || '',
      created_by: fullContactData.created_by,
      updated_by: fullContactData.updated_by,
    };

    setEditingContact(contactData);
    setIsEditModalOpen(true);
  };

  // Handle edit form submission
  const handleEditFormSubmit = async (formData) => {
    const payload = {
      divison: 'div',
      type: formData.contactType,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email,
      preferredName: formData.preferredName,
      jobTitle: formData.jobTitle,
      phone: {
        mobile: formData.mobileNum1,
        phone: formData.mobileNum2,
        Fax: formData.fax,
      },
      address: {
        address_line2: formData.address2,
        address_line1: formData.address1,
        city: formData.city,
        state: formData.state,
        zip: formData.zipCode,
        country: formData.country,
      },
      status: formData.vendorStatus || 'Active',
      contact_id: formData.contact_id,
    };

    try {
      await updateSalesContact(payload);
      setIsEditModalOpen(false);
      setEditingContact(null);

      // Refresh contacts data
      const { companyId, clientId } = getUserInfoDetails();
      const res = await axiosInstance.get('/contacts', {
        params: { company: companyId, client: clientId },
      });
      setAllContactsData(res.data.data.records || []);

      // Update the local contacts list with new data
      setContacts((prevContacts) =>
        prevContacts.map((c) => {
          if ((c.contact_id || c.contactId) === formData.contact_id) {
            return {
              ...c,
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              mobileNum1: formData.mobileNum1,
            };
          }
          return c;
        })
      );

      toast.success('Contact Updated Successfully');
    } catch (error) {
      toast.error('Failed to update contact. Please try again');
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingContact(null);
  };

  // Effect to add contact after modal closes
  useEffect(() => {
    if (pendingAdd) {
      setContacts((prevContacts) => {
        const currentContacts = Array.isArray(prevContacts) ? prevContacts : [];
        // Ensure contact_id is always string for comparison
        const isDuplicate = currentContacts.some(
          (c) => {
            return String(c.contact_id || c.contactId) === String(pendingAdd.contact_id);
          }
        );
        if (isDuplicate) {
          toast.warning("This contact has already been added");
          return currentContacts;
        }
        // Set isPrimary based on whether this is the first contact
        const newContact = {
          ...pendingAdd,
          isPrimary: currentContacts.length === 0,
        };
        const updatedContacts = [...currentContacts, newContact];
        // Immediately update context so contacts persist across tabs
        updateFormData("contacts", updatedContacts);
        return updatedContacts;
      });
      setPendingAdd(null);
    }
  }, [pendingAdd, updateFormData]);


  // Keep primaryContact/email in sync with the primary contact
  useEffect(() => {
    if (contacts.length > 0) {
      // Find the primary contact, or fallback to first contact
      const primaryContact = contacts.find((c) => c.isPrimary) || contacts[0];
      setValue(
        "primaryContact",
        `${primaryContact.firstName} ${primaryContact.lastName}`
      );
      setValue("email", primaryContact.email);
    } else {
      setValue("primaryContact", "");
      setValue("email", "");
    }
  }, [contacts, setValue]);

  // Check if contacts have changed compared to originalContacts (from context)
  const isDirty = useMemo(() => JSON.stringify(contacts) !== JSON.stringify(lastSavedContacts), [contacts, lastSavedContacts]);

  // Initialize contacts from formData only on mount and vendor change
  useEffect(() => {
    if (Array.isArray(formData?.contacts) && formData.contacts.length > 0) {
      // Transform contacts to ensure all fields are properly mapped
      const transformedContacts = formData.contacts.map((c, index) => ({
        ...c,
        firstName: c.firstName || c.first_name || '',
        lastName: c.lastName || c.last_name || '',
        mobileNum1: getPhoneNumber(c),
        isPrimary: c.isPrimary || c.contact_type === 'Primary' || index === 0,
      }));
      setContacts(transformedContacts);
      setLastSavedContacts(transformedContacts); // Sync dirty check baseline with transformed data
    } else {
      setContacts([]);
      setLastSavedContacts([]);
    }
    // eslint-disable-next-line
  }, []);


  const onSubmit = () => {
    if (isDirty) {
      updateFormData("contacts", contacts); // Update context, but don't save to backend yet
      setLastSavedContacts(contacts);
    }
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-y-6 mt-5 text-sm font-normal text-text-hint"
      >
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label>Primary Contact</label>
            <div className={`flex items-center px-3 py-2 mt-1 border rounded-lg h-11 ${errors.primaryContact ? "border-danger-500" : "border-neutral-500"}`}>
              <input
                {...register("primaryContact", {
                  required: "Primary contact is required",
                })}
                placeholder="(999) 999 9999"
                className="outline-none w-full text-black"
                readOnly
              />
              <img src={Pencil} alt="pencil" />
            </div>
            {errors.primaryContact && (
              <span className="text-danger-500">
                {errors.primaryContact.message}
              </span>
            )}
          </div>

          <div>
            <label>Email ID</label>
            <div className={`flex items-center px-3 py-2 mt-1 border rounded-lg h-11 ${errors.email ? "border-danger-500" : "border-neutral-500"}`}>
              <input
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                type="email"
                placeholder="Email ID"
                className="outline-none border-none w-full text-black focus:ring-0"
                readOnly
              />
              <img src={Pencil} alt="pencil" />
            </div>
            {errors.email && (
              <span className="text-danger-500">{errors.email.message}</span>
            )}
          </div>

          <div className="col-span-2 flex justify-end items-center gap-2">
            <button
              type="button"
              onClick={() => {
                updateFormData("contacts", contacts);
                setLastSavedContacts(contacts);
                navigate("/sales/vendors/summary/account-info");
              }}
              className="text-secondary-700 border border-secondary-700 py-2 px-5 rounded-full text-base bg-white hover:bg-gray-50 transition"
            >
              Previous
            </button>
            {!isCreating && hasUnsavedChanges && (
              <button
                type="button"
                disabled={isSaving}
                onClick={saveAllChanges}
                className="text-white bg-green-600 py-2 px-5 rounded-full text-base hover:bg-green-700 transition disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit(() => {
                updateFormData("contacts", contacts);
                setLastSavedContacts(contacts);
                if (isCreating) markTabComplete("contact");
                navigate("/sales/vendors/summary/billing");
              })}
              className="text-white bg-secondary-700 py-2 px-5 rounded-full text-base"
            >
              Next
            </button>
          </div>
        </div>
      </form>

      <div className="border border-gray-300 rounded-lg mt-5">
        <div className="flex justify-between items-center gap-2 p-3">
          <div className="flex flex-1 justify-end w-72">
           <button
              type="button"
              onClick={() => setIsContactsModalOpen(true)}
              className="bg-secondary-700 text-white border-2 border-secondary-500 rounded-full py-2 px-4 text-center"
            >
              Select Contact
            </button>
          </div>

          <Modal
            isOpen={isContactsOpen}
            onClose={() => setIsContactsModalOpen(false)}
          >
            <div className="p-4 relative flex flex-col gap-4">
              <button
                onClick={() => setIsContactsModalOpen(false)}
                className="absolute top-2 right-2 bg-secondary-700 text-white rounded-full px-3 py-1 text-sm hover:bg-secondary-800 focus:outline-none"
                aria-label="Close"
              >
                ×
              </button>
              <SalesContacts
                onSelectContact={handleContactSelect}
                isSelectionMode={true}
              />
              <div className="flex justify-end mt-4">
                <button
                  className="border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full"
                  onClick={() => setIsContactsModalOpen(false)}
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          </Modal>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-1/4">
                  Contact Name
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-1/6">
                  Phone Number
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-1/4">
                  Email
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-1/6">
                  Type
                </th>
                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-700 w-1/6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(contacts) && contacts.length > 0 ? (
                contacts.map((contact, index) => (
                  <tr
                    key={contact.contact_id || index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4">
                      <h3
                        onClick={() => handleEditContact(contact)}
                        className="text-base font-bold text-auxiliary-800 cursor-pointer hover:underline"
                      >
                        {`${contact.firstName} ${contact.lastName}`}
                      </h3>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{formatPhone(getPhoneNumber(contact)) || '-'}</td>
                    <td className="py-3 px-4 text-gray-600">{contact.email || '-'}</td>
                    <td className="py-3 px-4">
                      {contact.isPrimary ? (
                        <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700 font-medium">
                          Primary
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600">
                          Secondary
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {!contact.isPrimary && (
                        <button
                          type="button"
                          onClick={() => handleMakePrimary(contact.contact_id || contact.contactId)}
                          className="text-secondary-600 hover:text-secondary-800 text-xs px-2 py-1 border border-secondary-600 rounded-full hover:bg-secondary-50"
                        >
                          Make Primary
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-500">
                    No contacts available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Contact Modal */}
      <SalesContactsModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSubmit={handleEditFormSubmit}
        isEditMode={true}
        defaultValues={editingContact}
      />
    </div>
  );
};

export default Contacts;
